/**
 * ESIGN §7001(c) consent disclosure — the legal-gated, versioned text slot (CB-5.3).
 *
 * ✅ COUNSEL-APPROVED 2026-07-16 (CB-1.10 / OQ1 / DEP-2 gate closed). Reviewed against the US federal
 * ESIGN Act (15 U.S.C. §7001) + AZ UETA for US SMB consumer transactions; assessed launch-ready
 * ("low enforceability risk"). The five element texts below incorporate counsel's redlines. Any
 * future edit MUST bump ESIGN_CONSENT_VERSION (and re-confirm with counsel if substantive) — the
 * version string is captured with every consent_accepted event (server-side) so we can always prove
 * WHICH disclosure a given consumer accepted (FR-58).
 *
 * Separate follow-ups from the same review (NOT part of this consent gate): (1) add a standalone
 * "Electronic Records and Signatures" execution clause to the main contract agreement body; (2)
 * ensure the paper-copy fulfillment process actually exists + is documented.
 *
 * Tenant customization: the disclosure interpolates the sending company (paper-copy / withdrawal
 * channel) so it is tenant-specific today; a full per-tenant text override lands with the approved
 * wording (CB-1.10) behind this same version slot. Wording is not a UX decision (EXPERIENCE.md).
 */

/** Bump on ANY change to the disclosure text. `-draft` marks pre-counsel-approval versions so any
 *  consent recorded before sign-off is identifiable in the audit trail. */
export const ESIGN_CONSENT_VERSION = 'esign-7001c-v1-2026-07-16';

/** Counsel sign-off flag (CB-1.10). While false, the ceremony shows the pending-legal notice and the
 *  recorded version carries the `-draft` marker. Flip to true ONLY with counsel-approved text. */
export const ESIGN_CONSENT_APPROVED = true;

/** The affirmative control label — exact per UX-DR18 / FR-47. */
export const ESIGN_AFFIRMATIVE_LABEL = 'I agree to use electronic records and signatures';

export interface EsignDisclosureElement {
  /** Stable key for the §7001(c) element (audit/cert can reference these). */
  key: 'paper_right' | 'withdrawal' | 'scope' | 'hardware_software' | 'demonstration_of_access';
  heading: string;
  body: string;
}

/**
 * Build the full §7001(c) disclosure set (all five elements) for a consumer contract, interpolating
 * the sending company where a concrete channel is required. Counsel-approved wording (2026-07-16).
 */
export function buildEsignDisclosure(companyName: string): EsignDisclosureElement[] {
  const sender = companyName?.trim() || 'the sender';
  return [
    {
      key: 'paper_right',
      heading: 'Your right to a paper copy',
      body: `You have the right to receive this record on paper. To request a paper copy, contact ${sender} (reply to the email that sent you this link, or call them). We will provide it within a reasonable time, subject to any applicable reasonable fees.`,
    },
    {
      key: 'withdrawal',
      heading: 'Your right to withdraw consent',
      body: `You may withdraw your consent to use electronic records and signatures at any time before you finish signing. To withdraw, contact ${sender}. If you withdraw, this contract will not be completed electronically and you can arrange to sign on paper instead; ${sender} will tell you about any specific consequences or fees. Any obligations already incurred prior to withdrawal remain binding.`,
    },
    {
      key: 'scope',
      heading: 'What this consent covers',
      body: 'This consent applies to the electronic records and signatures for this contract only — not to any ongoing or future relationship.',
    },
    {
      key: 'hardware_software',
      heading: 'What you need to access and keep these records',
      body: 'To view, sign, and retain this record you need a current web browser, a device that can open and display PDF files, an internet connection, and a way to save or print the document (for example, to download the PDF). If these requirements change in a way that materially affects your ability to access the records, you will be asked to confirm consent again. Industry-standard measures are maintained to protect the integrity of these records.',
    },
    {
      key: 'demonstration_of_access',
      heading: 'Confirm you can open the document',
      body: 'Before you agree, please open the document below to confirm you can view it in this format. This makes sure you can actually access the record you are consenting to. By clicking "I agree" you acknowledge that you have successfully accessed the document and consent to proceed electronically.',
    },
  ];
}
